import React, { useState, useEffect } from 'react';
import { X, RotateCcw } from 'lucide-react';
import { reportingToday } from '../utils/reportingTime';

// Default time-frame presets — pages can override via the `presets` prop.
const DEFAULT_PRESETS = [
  { label: 'Today', days: '1' }, { label: '7d', days: '7' }, { label: '30d', days: '30' },
  { label: '90d', days: '90' }, { label: '1yr', days: '365' }, { label: 'All', days: 'all' },
];
/**
 * FilterDrawer — the right-hand slide-over of usage filters shared by every page that
 * shows usage analytics (My Usage, Users → View usage, LLM Stats). Each page declares
 * ONLY what it needs; the drawer owns all draft state and commits nothing until Apply.
 *
 *   <FilterDrawer
 *     open={open} onClose={() => setOpen(false)} theme={theme}
 *     value={{ dateFilter, model, product }}            // current committed filters
 *     selects={[                                          // dropdowns, rendered in order
 *       { key: 'model',   label: 'Model',   allLabel: 'All models',   options: models },
 *       { key: 'product', label: 'Product', allLabel: 'All products', options: products },
 *     ]}
 *     onApply={(next) => { setDateFilter(next.dateFilter); setModel(next.model); setProduct(next.product); }}
 *   />
 *
 * Contract:
 *  - `value.dateFilter` is `{ days, startDate, endDate }`. Each `selects[].key` reads/writes
 *    `value[key]` — a string, '' meaning unset ("All").
 *  - `onApply(next)` receives `{ dateFilter, ...oneKeyPerSelect }`; the page commits it (and
 *    typically refetches). Reset commits the defaults + cleared selects.
 *  - Time frame is always shown. By default it renders presets + an optional custom range
 *    (`allowCustomRange`). A page that already has its own time-frame widget can pass
 *    `renderTimeframe(dateFilter, setDateFilter, open)` and the drawer will render that
 *    instead — used by LLM Stats to keep its always-visible DateRangeFilter.
 *  - A select whose `options` is empty is skipped — pass all of them and let data decide.
 *  - `options` items may be plain strings, or `{ value, label }` for id/label pairs (e.g. users).
 *  - A select with `lead: true` renders ABOVE Time frame, decoupled from the rest — for
 *    something like a context switcher. Every other select renders below it, in order.
 */
export default function FilterDrawer({
  open, onClose, onApply, value = {}, selects = [], theme,
  presets = DEFAULT_PRESETS, defaults = { days: '7', startDate: null, endDate: null },
  allowCustomRange = true, renderTimeframe = null, title = 'Filters',
}) {
  // Evaluate during render rather than module load so a tab left open across
  // IST midnight immediately permits the new calendar date.
  const todayStr = reportingToday();
  const [df, setDf] = useState(value.dateFilter || defaults);
  const [sel, setSel] = useState(() => Object.fromEntries(selects.map(s => [s.key, value[s.key] || ''])));
  const [showCustom, setShowCustom] = useState(false);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [rangeError, setRangeError] = useState('');

  // Seed the drafts from the committed value each time the drawer OPENS. Done in the
  // render phase (not an effect) so children — including a `renderTimeframe` widget that
  // seeds its own internal state on mount — see the fresh value on the very first render
  // after opening. This is React's "adjust state when a prop changes" pattern — tracked
  // with useState, not useRef: a plain ref mutation during render leaks across Strict
  // Mode's double-render-invocation and gets silently skipped on a freshly-remounted
  // instance (e.g. a parent that unmounts this drawer while loading, like MyUsage.js).
  const [prevOpen, setPrevOpen] = useState(open);
  if (prevOpen !== open) {
    setPrevOpen(open);
    if (open) {
      const dv = value.dateFilter || defaults;
      setDf(dv);
      setSel(Object.fromEntries(selects.map(s => [s.key, value[s.key] || ''])));
      const custom = !!(dv.startDate && dv.endDate);
      setShowCustom(custom);
      setStart(custom ? dv.startDate : '');
      setEnd(custom ? dv.endDate : '');
      setRangeError('');
    }
  }

  // Escape closes the drawer.
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open, onClose]);

  const commit = (nextDf) => onApply({ dateFilter: nextDf, ...sel });
  const apply = () => {
    // The inline custom range is validated here; a `renderTimeframe` widget validates
    // itself and writes the range straight into `df`, so we just commit df in that case.
    if (!renderTimeframe && allowCustomRange && showCustom) {
      if (!start || !end) { setRangeError('Pick both start and end dates.'); return; }
      if (start > end) { setRangeError('Start date must be on or before the end date.'); return; }
      commit({ days: null, startDate: start, endDate: end });
    } else {
      commit(df);
    }
    onClose();
  };
  const reset = () => {
    onApply({ dateFilter: defaults, ...Object.fromEntries(selects.map(s => [s.key, ''])) });
    onClose();
  };

  // opt-in per select (not positional) so an existing page's first select,
  // e.g. Model, doesn't silently jump above Time frame too.
  const visibleSelects = selects.filter(s => (s.options || []).length > 0);
  const leadSelect = visibleSelects.find(s => s.lead);
  const restSelects = visibleSelects.filter(s => s !== leadSelect);
  const renderSelect = (s) => (
    <div key={s.key}>
      <label className="block text-xs font-medium text-fg-muted uppercase tracking-wide mb-2">{s.label}</label>
      <select
        value={sel[s.key] || ''}
        onChange={e => setSel(prev => ({ ...prev, [s.key]: e.target.value }))}
        className="w-full px-3 py-1.5 bg-field border border-field-border rounded-lg text-fg text-sm"
      >
        <option value="">{s.allLabel || `All ${s.label.toLowerCase()}`}</option>
        {s.options.map(o => {
          const val = typeof o === 'string' ? o : o.value;
          const lbl = typeof o === 'string' ? o : o.label;
          return <option key={val} value={val}>{lbl}</option>;
        })}
      </select>
    </div>
  );

  return (
    <div className={`fixed inset-0 z-[60] ${open ? '' : 'pointer-events-none'}`}>
      <div
        onClick={onClose}
        className={`absolute inset-0 bg-black/40 transition-opacity duration-200 ${open ? 'opacity-100' : 'opacity-0'}`}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`absolute right-0 top-0 h-full w-[340px] max-w-[90vw] bg-panel border-l border-panel-border shadow-2xl flex flex-col transition-transform duration-200 ${open ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-panel-border">
          <h2 className="text-lg font-semibold text-fg">{title}</h2>
          <button onClick={onClose} aria-label="Close filters" className="text-fg-muted hover:text-fg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
          {leadSelect && renderSelect(leadSelect)}

          <div>
            <label className="block text-xs font-medium text-fg-muted uppercase tracking-wide mb-2">Time frame</label>
            {renderTimeframe ? renderTimeframe(df, setDf, open) : (
              <>
                <div className="flex flex-wrap gap-1.5">
                  {presets.map(p => (
                    <button
                      key={p.days}
                      onClick={() => { setShowCustom(false); setDf({ days: p.days, startDate: null, endDate: null }); }}
                      className={`px-2 py-1 rounded-lg text-xs font-medium border transition-colors ${!showCustom && df.days === p.days ? 'bg-orange text-white border-orange' : 'bg-field border-field-border text-fg hover:border-orange/50'}`}
                    >{p.label}</button>
                  ))}
                  {allowCustomRange && (
                    <button
                      onClick={() => setShowCustom(true)}
                      className={`px-2 py-1 rounded-lg text-xs font-medium border transition-colors ${showCustom ? 'bg-orange text-white border-orange' : 'bg-field border-field-border text-fg hover:border-orange/50'}`}
                    >Custom</button>
                  )}
                </div>
                {allowCustomRange && showCustom && (
                  <div className="mt-2">
                    <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs ${rangeError ? 'border-red-500 bg-red-500/10' : 'border-field-border bg-field'}`}>
                      <input
                        type="date" value={start} max={end || todayStr}
                        onChange={e => {
                          const newStart = e.target.value;
                          setStart(newStart);
                          // Clearing Start disables End below — clear its stale
                          // value too, or it's stuck showing an old date with no
                          // way to clear it (End is disabled while Start is empty).
                          if (!newStart) setEnd('');
                          setRangeError('');
                        }}
                        style={{ colorScheme: theme === 'light' ? 'light' : 'dark' }}
                        className="ls-date-input bg-transparent text-fg text-xs outline-none w-[7rem]"
                      />
                      <span className="text-fg-muted">→</span>
                      <input
                        type="date" value={end} min={start || undefined} max={todayStr}
                        disabled={!start}
                        onChange={e => { setEnd(e.target.value); setRangeError(''); }}
                        style={{ colorScheme: theme === 'light' ? 'light' : 'dark' }}
                        className="ls-date-input bg-transparent text-fg text-xs outline-none w-[7rem] disabled:opacity-40 disabled:cursor-not-allowed"
                      />
                    </div>
                    {rangeError && <p className="text-red-500 text-xs mt-1">{rangeError}</p>}
                  </div>
                )}
              </>
            )}
          </div>

          {restSelects.map(renderSelect)}
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-4 border-t border-panel-border">
          <button onClick={reset} className="flex items-center gap-1.5 px-3 py-2 text-fg-muted hover:text-fg text-sm transition-colors">
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 bg-field border border-field-border hover:border-orange/50 text-fg rounded-lg text-sm transition-colors">Cancel</button>
            <button onClick={apply} className="px-4 py-2 bg-orange hover:bg-orange-hover text-white rounded-lg text-sm font-medium transition-colors">Apply</button>
          </div>
        </div>
      </div>
    </div>
  );
}
